import _ from "lodash";

/**
 * Convert Json keys to snake_case
 * Return the same object as input to prevent caller component to be rerendered.
 *
 * @param body
 * @returns body
 *
 */
export const toSnakeJSON = (body: { [key: string | number]: any }): any => {
  const keys = Object.keys(body);
  for (let key of keys) {
    const tmp = body[key];
    delete body[key];
    body[_.snakeCase(key)] = tmp;
  }
  return body;
};

/**
 * Convert Json keys to camelCase
 * Return the same object as input to prevent caller component to be rerendered.
 *
 * @param body
 * @returns body
 *
 */
export const toCamelJSON = (body: { [key: string | number]: any }): any => {
  const keys = Object.keys(body);
  for (let key of keys) {
    const tmp = body[key];
    delete body[key];
    body[_.camelCase(key)] = tmp;
  }
  return body;
};

const isPlainObject = (value: any): value is { [key: string | number]: any } =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const convertKeysDeep = (
  value: any,
  convertKey: (key: string) => string
): any => {
  if (Array.isArray(value)) {
    return value.map((item) => convertKeysDeep(item, convertKey));
  }
  if (!isPlainObject(value)) {
    return value;
  }
  return Object.keys(value).reduce<{ [key: string]: any }>((converted, key) => {
    converted[convertKey(key)] = convertKeysDeep(value[key], convertKey);
    return converted;
  }, {});
};

/**
 * Convert all object keys in a JSON-compatible payload to snake_case.
 *
 * Schema APIs contain nested field and answer arrays, so they need deep
 * conversion. The legacy converters above are intentionally left shallow
 * because many existing request/response helpers rely on their current scope.
 */
export const toSnakeJSONDeep = (body: any): any =>
  convertKeysDeep(body, _.snakeCase);

/**
 * Convert all object keys in a JSON-compatible payload to camelCase.
 *
 * Use this for backend-owned schema responses where nested fields such as
 * `fields[].field_id` are used as form field names by React Final Form.
 */
export const toCamelJSONDeep = (body: any): any =>
  convertKeysDeep(body, _.camelCase);
